/**
 * 💜 HEARTBEATS TEASER - Le Secret de l'Épicerie
 * 
 * UN SEUL BLOC SUPER CHILL !
 * - Secret Clients + Couples/Timides fusionnés
 * - Design spatial et mystérieux
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Sparkles, Users, MessageCircle, Radio, Lock, Coffee } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HeartbeatsTeaserSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-16 px-4 overflow-hidden">
      {/* Fond mystérieux avec effet pulse */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(236, 72, 153, 0.08) 0%, transparent 70%)'
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      </div>

      <div className="max-w-3xl mx-auto relative">
        
        {/* BLOC UNIQUE FUSIONNÉ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 md:p-10 rounded-3xl text-center space-y-8"
          style={{
            background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(236, 72, 153, 0.05), rgba(99, 102, 241, 0.05))',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            boxShadow: '0 0 60px rgba(168, 85, 247, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
          }}
        >
          {/* Badge secret */}
          <motion.div
            initial={{ scale: 0.9 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30"
          >
            <Lock className="w-4 h-4 text-pink-400" />
            <span className="text-pink-300 text-sm font-bold uppercase tracking-wider">🤫 Secret Clients</span>
          </motion.div>

          {/* Titre mystérieux */}
          <div className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-black text-white">
              Et si <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-pink-400">quelque chose</span> se passait ici...
            </h2>

            <p className="text-gray-400">
              Nos clients fidèles ont accès à <span className="text-pink-400 font-semibold">quelque chose d'unique</span>...
              <br />
              Un secret bien gardé. Une expérience réservée aux habitués.
            </p>
          </div>

          {/* Indices mystérieux */}
          <div className="flex flex-wrap justify-center gap-3">
            <motion.div
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20"
              whileHover={{ scale: 1.05 }}
            >
              <Radio className="w-4 h-4 text-purple-400 animate-pulse" />
              <span className="text-purple-300 text-sm">Des voix...</span>
            </motion.div>
            
            <motion.div
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/20"
              whileHover={{ scale: 1.05 }}
            >
              <Users className="w-4 h-4 text-pink-400" />
              <span className="text-pink-300 text-sm">Des connexions...</span>
            </motion.div>
            
            <motion.div
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20"
              whileHover={{ scale: 1.05 }}
            >
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-300 text-sm">De la magie...</span>
            </motion.div>
          </div>

          {/* Séparateur subtil */}
          <div className="flex items-center gap-4 py-2">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
            <div className="flex gap-2">
              <motion.span 
                className="text-2xl"
                animate={{ y: [0, -3, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0 }}
              >
                👫
              </motion.span>
              <motion.span 
                className="text-2xl"
                animate={{ y: [0, -3, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
              >
                🫂
              </motion.span>
              <motion.span 
                className="text-2xl"
                animate={{ y: [0, -3, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
              >
                ☕
              </motion.span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
          </div>

          {/* Section Couples/Timides */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">
              💑 En couple ? Timide ? <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">C'est aussi pour vous !</span>
            </h3>

            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              Pas besoin d'être célib' ! <span className="text-cyan-400">Papotez sans pression</span>, 
              faites-vous des <span className="text-pink-400">amis couples</span>, ou rejoignez nos{' '}
              <span className="text-purple-400">salons détente</span> pour discuter de tout et de rien...{' '}
              <span className="text-amber-400">comme au comptoir de l'épicerie</span> 😉
            </p>

            {/* Mini tags */}
            <div className="flex flex-wrap justify-center gap-2">
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 text-gray-400 text-xs">
                <MessageCircle className="w-3 h-3 text-cyan-400" />
                Discussions
              </span>
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 text-gray-400 text-xs">
                <Coffee className="w-3 h-3 text-amber-400" />
                Ambiance détente
              </span>
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 text-gray-400 text-xs">
                <Users className="w-3 h-3 text-purple-400" />
                Gens du coin
              </span>
            </div>
          </div>

          {/* CTA + Coeur */}
          <div className="flex flex-col items-center gap-4 pt-4">
            {/* Coeur animé */}
            <motion.div
              className="relative"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Heart className="w-10 h-10 text-pink-500 fill-pink-500/30" />
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(236, 72, 153, 0.4) 0%, transparent 70%)' }}
                animate={{ scale: [1, 1.8], opacity: [0.5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </motion.div>

            {/* Bouton */}
            <motion.button
              onClick={() => navigate('/client')}
              className="group px-6 py-3 rounded-xl font-bold text-white"
              style={{
                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.3), rgba(168, 85, 247, 0.3))',
                border: '1px solid rgba(236, 72, 153, 0.4)',
                boxShadow: '0 0 20px rgba(236, 72, 153, 0.2)'
              }}
              whileHover={{ 
                scale: 1.02,
                boxShadow: '0 0 40px rgba(236, 72, 153, 0.4)'
              }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="flex items-center gap-2">
                🔮 Découvrir le secret
                <motion.span
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  →
                </motion.span>
              </span>
            </motion.button>

            <p className="text-gray-500 text-xs">
              Gratuit • Réservé aux clients de l'épicerie
            </p>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default HeartbeatsTeaserSection;
